export type NavCityItem = {
	label: string;
	href: string;
	subLinks?: NavCityItem[];
};

export type NavDropdownKey = "proyectos" | "venta" | "arriendo";

export type NavDropdownPanel = {
	key: NavDropdownKey;
	title: string;
	seeAllHref: string;
	cities: NavCityItem[];
	variant?: "default" | "simple";
};

/** IDs de header_links del CMS excluidos del nav CO (desktop y mobile). */
export const FINCARAIZ_EXCLUDED_HEADER_LINK_IDS = ["51", "56"];

/** Mapeo header_link.id → clave de dropdown. */
export const FINCARAIZ_DROPDOWN_BY_HEADER_LINK_ID: Record<string, NavDropdownKey> = {
	"47": "proyectos",
	"48": "venta",
	"49": "arriendo",
};

export const FINCARAIZ_DROPDOWN_TITLES: Record<NavDropdownKey, string> = {
	proyectos: "PROYECTOS NUEVOS - COLOMBIA",
	venta: "VENTA DE INMUEBLES - COLOMBIA",
	arriendo: "ARRIENDOS - COLOMBIA",
};

export const FINCARAIZ_SEE_ALL_DEFAULTS: Record<NavDropdownKey, string> = {
	proyectos: "/proyectos-vivienda",
	venta: "/venta",
	arriendo: "/arriendo",
};


const FINCARAIZ_NAV_CITIES_SOURCE: Record<NavDropdownKey, NavCityItem[]> = {
	venta: [
		{ label: "Bogotá", href: "/venta/bogota/bogota-dc" },
		{ label: "Medellín", href: "/venta/medellin/antioquia" },
		{ label: "Cali", href: "/venta/cali/valle-del-cauca" },
		{ label: "Barranquilla", href: "/venta/barranquilla/atlantico" },
		{ label: "Bucaramanga", href: "/venta/bucaramanga/santander" },
		{ label: "Cartagena", href: "/venta/cartagena/bolivar" },
	],
	arriendo: [
		{ label: "Bogotá", href: "/arriendo/bogota/bogota-dc" },
		{ label: "Medellín", href: "/arriendo/medellin/antioquia" },
		{ label: "Cali", href: "/arriendo/cali/valle-del-cauca" },
		{ label: "Barranquilla", href: "/arriendo/barranquilla/atlantico" },
		{ label: "Bucaramanga", href: "/arriendo/bucaramanga/santander" },
		{ label: "Cartagena", href: "/arriendo/cartagena/bolivar" },
	],
	proyectos: [
		{ label: "Bogotá", href: "/proyectos-vivienda/bogota/bogota-dc" },
		{ label: "Cundinamarca", href: "/proyectos-vivienda/cundinamarca/cundinamarca" },
		{ label: "Medellín", href: "/proyectos-vivienda/medellin/antioquia" },
		{ label: "Cali", href: "/proyectos-vivienda/cali/valle-del-cauca" },
		{ label: "Barranquilla", href: "/proyectos-vivienda/barranquilla/atlantico" },
		{ label: "Pereira", href: "/proyectos-vivienda/pereira/risaralda" },
		{ label: "Santa Marta", href: "/proyectos-vivienda/santa-marta/magdalena" },
		{ label: "Bucaramanga", href: "/proyectos-vivienda/bucaramanga/santander" },
	],
};

/** Ciudades del mega menú header (Venta/Arriendo). Separado de FINCARAIZ_NAV_CITIES_SOURCE para no afectar otras secciones. */
const FINCARAIZ_HEADER_MEGA_CITY_PATHS: { label: string; path: string }[] = [
	{ label: "Bogotá", path: "bogota/bogota-dc" },
	{ label: "Cundinamarca", path: "cundinamarca/cundinamarca" },
	{ label: "Medellín", path: "medellin/antioquia" },
	{ label: "Cali", path: "cali/valle-del-cauca" },
	{ label: "Barranquilla", path: "barranquilla/atlantico" },
	{ label: "Pereira", path: "pereira/risaralda" },
	{ label: "Santa Marta", path: "santa-marta/magdalena" },
	{ label: "Bucaramanga", path: "bucaramanga/santander" },
];

const MEGA_SUBLINK_LABELS: Record<"venta" | "arriendo", { apartamentos: string; casas: string }> = {
	venta: {
		apartamentos: "Apartamentos en venta",
		casas: "Casas en venta",
	},
	arriendo: {
		apartamentos: "Apartamentos en arriendo",
		casas: "Casas en arriendo",
	},
};

const buildMegaSubLinks = (cityHref: string, operation: "venta" | "arriendo"): NavCityItem[] => {
	const labels = MEGA_SUBLINK_LABELS[operation];
	return [
		{ label: labels.apartamentos, href: `${cityHref}/apartamentos` },
		{ label: labels.casas, href: `${cityHref}/casas` },
	];
};

const buildHeaderMegaCities = (prefix: "/venta" | "/arriendo"): NavCityItem[] =>
	FINCARAIZ_HEADER_MEGA_CITY_PATHS.map(({ label, path }) => {
		const href = `${prefix}/${path}`;
		const operation = prefix === "/venta" ? "venta" : "arriendo";
		return {
			label,
			href,
			subLinks: buildMegaSubLinks(href, operation),
		};
	});


export const getFincaraizNavCities = (): Record<NavDropdownKey, NavCityItem[]> =>
	FINCARAIZ_NAV_CITIES_SOURCE;

export const buildFincaraizDropdownPanel = (
	key: NavDropdownKey,
	seeAllHref?: string
): NavDropdownPanel => {
	const cities =
		key === "venta"
			? buildHeaderMegaCities("/venta")
			: key === "arriendo"
				? buildHeaderMegaCities("/arriendo")
				: getFincaraizNavCities()[key];

	const panel: NavDropdownPanel = {
		key,
		title: FINCARAIZ_DROPDOWN_TITLES[key],
		seeAllHref: seeAllHref || FINCARAIZ_SEE_ALL_DEFAULTS[key],
		cities,
	};

	if (key === "proyectos") {
		panel.variant = "simple";
		panel.cities = panel.cities.map(city =>
			city.label === "Pereira"
				? {
						...city,
						subLinks: [
							{ label: "Vivienda VIS", href: "/proyectos-vivienda/vivienda-vis" },
						],
					}
				: city
		);
	}

	return panel;
};
